import React from 'react';
import { FREEMIUM_MESSAGES, FreemiumMessageKey } from '../utils/freemiumLimits';
import { FeatureKey } from '../utils/features';

interface FeatureLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (feature: FeatureKey) => void;
  messageKey?: FreemiumMessageKey;
  // Custom overrides
  customTitle?: string;
  customMessage?: string;
  customFeature?: FeatureKey;
}

const FeatureLimitModal: React.FC<FeatureLimitModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  messageKey,
  customTitle,
  customMessage,
  customFeature,
}) => {
  if (!isOpen) return null;

  // Get message content from predefined messages or use custom
  const messageConfig = messageKey ? FREEMIUM_MESSAGES[messageKey] : null;
  const title = customTitle || messageConfig?.title || 'Premium Feature';
  const message = customMessage || messageConfig?.message || 'This feature requires a Premium subscription.';
  const feature = customFeature || messageConfig?.feature || 'cloudSync';

  const handleUpgrade = () => {
    onUpgrade(feature);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 overflow-hidden">
        {/* Premium gradient accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500" />
        
        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
              <span className="text-4xl">✨</span>
            </div>
          </div>
          
          {/* Title */}
          <h3 className="text-xl font-bold text-white text-center mb-3">
            {title}
          </h3>
          
          {/* Message */}
          <p className="text-gray-300 text-center mb-6 leading-relaxed">
            {message}
          </p>
          
          {/* Premium Benefits */}
          <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-400 mb-3 text-center uppercase tracking-wide">
              Premium includes:
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-gray-300">
                <span className="text-green-400">✓</span>
                Unlimited charts & setlists
              </li>
              <li className="flex items-center gap-2 text-gray-300">
                <span className="text-green-400">✓</span>
                Full annotation editing
              </li>
              <li className="flex items-center gap-2 text-gray-300">
                <span className="text-green-400">✓</span>
                PDF export for gigs
              </li>
              <li className="flex items-center gap-2 text-gray-300">
                <span className="text-green-400">✓</span>
                Unlimited Jam Session collaborators
              </li>
              <li className="flex items-center gap-2 text-gray-300">
                <span className="text-green-400">✓</span>
                Cloud sync across devices
              </li>
            </ul>
          </div>
          
          {/* Price */}
          <div className="text-center mb-6">
            <div className="text-gray-400 text-sm">Starting at</div>
            <div className="text-2xl font-bold text-white">
              €1.20<span className="text-base font-normal text-gray-400">/month</span>
            </div>
            <div className="text-xs text-cyan-400 mt-1">Less than a coffee ☕</div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg font-medium transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={handleUpgrade}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-lg font-semibold transition-all transform hover:scale-[1.02] shadow-lg"
            >
              🚀 Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureLimitModal;
